// UI-CLAIM-HEADER-1 (2026-05-07) — sticky compact claim header per memo
// §15.3.  Back button and action buttons fold into the header itself so
// the header is the first visible element in .page and sticks at top:0
// within the scroll container.  Meta row (DOL / Adjuster / Vessel / HIPAA)
// stays visible while scrolling tab content.
//
// UI-CLAIM-ACTIVITY-1 (2026-05-07) — Activity tab added at position 1.
// General demoted to position 2.  Default landing tab stays "general" for
// one cycle; Activity-default cutover lands in UI-CLAIM-ACTIVITY-2 alongside
// the GET /claims/{id}/activity multi-source SQL view.  v1 feed is diary-only.
import { useState, useEffect } from "react";
import { apiFetch, fmt, statusColor, can, canOnClaim, getUser } from "../api";
import { useNavigation } from "../components/nav/NavigationContext";
import BordereauReadinessChip from "../components/BordereauReadinessChip";
import { DocumentsEmailsTab, TimeExpensesTab, GeneralTab, PartiesTab, ClaimLoss } from "../components/ClaimTabs";
import { OverviewTab, ActivityWorkspace, FinancialsWorkspace, ReportsWorkspace, ClaimFinancialsSummary } from "../components/ClaimWorkspace";
import ClaimantTab from "../components/ClaimantTab";
import PolicyCoverageTab from "../components/PolicyCoverageTab";  // CLAIM-WS-IA B2: flatten Claim Info → top tabs
import { ReadinessHeaderCheck } from "../components/ClaimReadiness";

// CLAIM-WORKSPACE-UX-1 (2026-06-02) — consolidated 7-tab claim workspace
// (was 14 tabs). The old tab ids still arrive via deep-links (notifications,
// focusBillId), so TAB_ALIAS maps any old id to its new home.
// CLAIM-OVERVIEW-UX-3 tab structure: Claim Info is a container (Claim Details /
// Claimant / Parties / Policy & Coverage sub-tabs); Time is its own Timelog tab;
// Activity moves to the end.
// CLAIM-WS-IA B2 (2026-06-24): top tabs now MIRROR the Overview domain cards —
// Claim Info's old sub-tabs (Claim/Claimant/Parties/Coverage) are promoted to
// top level so each domain is one click. Old ids still arrive via deep-links and
// are redirected through TAB_ALIAS below.
const TABS = [
  {id:"overview",   label:"Overview"},
  {id:"claim",      label:"Claim"},
  {id:"claimant",   label:"Claimant"},
  {id:"parties",    label:"Parties"},
  {id:"coverage",   label:"Coverage"},
  {id:"financials", label:"Claim Financials"},
  {id:"timelog",    label:"Timelog"},
  {id:"documents",  label:"Documents"},
  {id:"reports",    label:"Reports"},
  {id:"activity",   label:"Activity"},
];
const TAB_ALIAS = {
  general:"claim", overview:"overview",
  claiminfo:"claim", claimdetails:"claim", claim:"claim",
  claimant:"claimant", parties:"parties", policy:"coverage", coverage:"coverage",
  activity:"activity", notes:"activity", tasks:"activity", feed:"activity",
  docs:"documents", documents:"documents",
  payments:"financials", reserves:"financials", bills:"financials", reimb:"financials", billing:"financials", financials:"financials",
  timeexp:"timelog", timelog:"timelog",
  reports:"reports", bordereau:"reports",
};
const toTab = (id) => TAB_ALIAS[id] || "overview";

// CLAIMANT-INJURY-DASHBOARD-1 (P6) / CLAIM-OVERVIEW-UX-2: attention items + Overview
// quick actions deep-link to the SUB-tab that owns the work. The id is a sub-level
// id (bills/payments/reserves/timeexp/notes/tasks/…); toTab() maps it to the main
// tab and the workspace opens straight to that sub-tab via initialSub.
const ATTN_SUB = {
  RESERVE_REVIEW: "reserves", PAYMENT_REVIEW: "bills", PAYMENT_CODING_REQUIRED: "payments",
  OVERDUE_TASK: "tasks", HIGH_PRIORITY_TASK: "tasks", DOCUMENT_REVIEW: "documents",
  MISSING_CLAIMANT_INFO: "claimant",
};
const FIN_SUBS = new Set(["bills", "payments", "reserves", "reimb", "billing"]);
const CINFO_SUBS = new Set(["claimdetails", "claimant", "parties", "policy"]);
const ACT_SUBS = new Set(["feed", "notes", "tasks"]);

// IA-TPA-SEPARATION-1 (Q10) — Related-matter strip. On a TPA matter: shows the
// linked IA file + Link/Unlink (TPA-authorized only). On an IA file: shows a
// related TPA matter ONLY if the viewer can see it (server /related is access-
// gated, so an IA-only user gets [] and this renders nothing). Linking renames
// the TPA matter to "<IA number>-TPA".
function IaPicker({ tpaId, onClose, onLinked }) {
  const [q, setQ] = useState(""); const [results, setResults] = useState([]); const [err, setErr] = useState("");
  useEffect(() => {
    if (q.trim().length < 2) { setResults([]); return; }
    const h = setTimeout(() => apiFetch(`/tpa/ia-lookup?q=${encodeURIComponent(q.trim())}`).then(setResults).catch(() => setResults([])), 250);
    return () => clearTimeout(h);
  }, [q]);
  const pick = async (iaId) => {
    setErr("");
    try { onLinked(await apiFetch(`/claims/${tpaId}/link-ia`, { method: "POST", body: { ia_claim_id: iaId } })); }
    catch (e) { setErr(e.message); }
  };
  return (<div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
    <div className="modal">
      <div className="modal-header"><h3>Link to IA File</h3><button className="modal-close" onClick={onClose}>×</button></div>
      <div className="modal-body">
        {err && <div className="alert-error">{err}</div>}
        <input autoFocus placeholder="Search IA claim # or insured…" value={q} onChange={e => setQ(e.target.value)}
               style={{ width: "100%", padding: 8, marginBottom: 8 }} />
        <div style={{ maxHeight: 320, overflow: "auto", border: "1px solid #eee", borderRadius: 4 }}>
          {results.map(r => (
            <div key={r.id} onClick={() => pick(r.id)} style={{ padding: "8px 10px", borderBottom: "1px solid #eee", cursor: "pointer" }}>
              <span className="mono" style={{ color: "#2563eb" }}>{r.claim_number}</span>
              {r.claimant_name && <span style={{ color: "#666", marginLeft: 8 }}>{r.claimant_name}</span>}
              <span style={{ color: "#999", marginLeft: 8, fontSize: 12 }}>{r.claim_status}</span>
            </div>
          ))}
          {q.trim().length >= 2 && results.length === 0 && <div style={{ padding: 10, color: "#999" }}>No matching IA files.</div>}
        </div>
        <p style={{ fontSize: 12, color: "#666", marginTop: 8 }}>Linking records the relationship between this TPA matter and the IA file — the TPA claim number is independent and is not changed.</p>
      </div>
    </div></div>);
}

function RelatedMatters({ claim, onClaimUpdated }) {
  const [related, setRelated] = useState([]);
  const [showPicker, setShowPicker] = useState(false);
  const isTpa = claim.matter_type === "tpa";
  const canLink = can("create_tpa_file");
  const load = () => apiFetch(`/claims/${claim.id}/related`).then(setRelated).catch(() => setRelated([]));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [claim.id]);
  const linkedIa = related.find(r => r.matter_type === "ia");
  const relatedTpa = related.filter(r => r.matter_type === "tpa");
  const unlink = async () => {
    if (!window.confirm("Unlink this TPA matter from its IA file? The claim number is not affected.")) return;
    try { const res = await apiFetch(`/claims/${claim.id}/unlink-ia`, { method: "POST" }); onClaimUpdated({ ...claim, claim_number: res.claim_number }); load(); }
    catch (e) { window.alert(e.message); }
  };
  if (!isTpa && relatedTpa.length === 0) return null;   // no clutter on ordinary IA claims
  const wrap = { margin: "8px 0", padding: "8px 14px", background: "#f5f3ff", border: "1px solid #ddd6fe", borderRadius: 6, fontSize: 13, color: "#4c1d95" };
  return (
    <div style={wrap}>
      {isTpa ? (
        linkedIa ? (
          <span>🔗 Linked IA file: <strong>{linkedIa.claim_number}</strong> <span style={{ color: "#7c3aed" }}>({linkedIa.claim_status})</span>
            {canLink && <button className="btn btn-sm" onClick={unlink} style={{ marginLeft: 10 }}>Unlink</button>}</span>
        ) : (
          <span>This TPA matter is not linked to an IA file.
            {canLink && <button className="btn btn-sm btn-blue" onClick={() => setShowPicker(true)} style={{ marginLeft: 10 }}>Link to IA File</button>}</span>
        )
      ) : (
        <span>🔗 Related TPA matter: {relatedTpa.map(r => <strong key={r.claim_id} style={{ marginRight: 10 }}>{r.claim_number} <span style={{ color: "#7c3aed", fontWeight: 400 }}>({r.claim_status})</span></strong>)}</span>
      )}
      {showPicker && <IaPicker tpaId={claim.id} onClose={() => setShowPicker(false)}
        onLinked={(res) => { setShowPicker(false); onClaimUpdated({ ...claim, claim_number: res.claim_number }); load(); }} />}
    </div>
  );
}

// Admin VOID controls (TPA-MATTER-CREATION-1). system_admin only (Cap.VOID_CLAIM).
// Reversible soft-void: hides the claim everywhere, keeps data + audit trail.
function VoidControls({ claim, onClaimUpdated }) {
  const [busy, setBusy] = useState(false);
  if (!can("void_claim")) return null;
  const refresh = async () => { try { onClaimUpdated(await apiFetch(`/claims/${claim.id}`)); } catch { /* ignore */ } };
  const doVoid = async () => {
    const reason = window.prompt("Void this claim? It will be hidden from all lists, search, reports and pickers. Data is kept and this can be reversed.\n\nReason (required):");
    if (reason == null) return;
    if (!reason.trim()) { window.alert("A reason is required to void a claim."); return; }
    setBusy(true);
    try { await apiFetch(`/claims/${claim.id}/void`, { method: "POST", body: { reason: reason.trim() } }); await refresh(); }
    catch (e) { window.alert(e.message || "Void failed."); } finally { setBusy(false); }
  };
  const doUnvoid = async () => {
    if (!window.confirm("Restore this voided claim? It will reappear in lists, search and reports.")) return;
    setBusy(true);
    try { await apiFetch(`/claims/${claim.id}/unvoid`, { method: "POST" }); await refresh(); }
    catch (e) { window.alert(e.message || "Un-void failed."); } finally { setBusy(false); }
  };
  return claim.voided_at
    ? <button className="btn" disabled={busy} onClick={doUnvoid} title="Restore this claim">Un-void</button>
    : <button className="btn" disabled={busy} onClick={doVoid} style={{ color: "#b91c1c" }} title="Void this claim (system admin)">Void</button>;
}

export default function ClaimDetail({ claimId, onBack, openTime, openTask, onWorkChanged, workRefresh, initialTab, focusBillId, focusTaskId }) {
  const [claim, setClaim]         = useState(null);
  const [loading, setLoading]     = useState(true);
  const [activeTab, setActiveTab] = useState(toTab(initialTab));
  // focusSub carries the desired sub-tab when a deep-link (quick action / attention
  // item) targets a workspace that has its own sub-nav. Set together with activeTab.
  const [focusSub, setFocusSub]   = useState(initialTab === "bills" || initialTab === "tasks" ? initialTab : null);
  const goWithSub = (id) => { setActiveTab(toTab(id)); setFocusSub(id); };
  const { reportLabel, recordVisit } = useNavigation();

  useEffect(() => {
    setLoading(true);
    apiFetch(`/claims/${claimId}`)
      .then(d => { setClaim(d); setLoading(false); })
      .catch(() => setLoading(false));
  }, [claimId]);
  // S3 (audit): silently re-fetch the claim header/status on a global work bump
  // (no loading flash) so reserve/payment/status changes reflect immediately.
  useEffect(() => {
    if (!workRefresh) return;
    apiFetch(`/claims/${claimId}`).then(setClaim).catch(() => {});
  }, [claimId, workRefresh]);
  // GLOBAL-NAV-1: surface the real claim # for breadcrumbs / recents / pins,
  // keyed to THIS claim's own location (async load may finish off-screen), and
  // record the visit in the per-user Recents.
  useEffect(() => {
    if (!claim?.claim_number) return;
    reportLabel(claim.claim_number, `claim-detail:${claimId}`);
    recordVisit({ kind: "claim", ref_id: String(claimId), page: "claim-detail", label: claim.claim_number, params: { claimId } });
  }, [claim?.claim_number, claimId, reportLabel, recordVisit]);
  // Deep-link (notification → bill review): jump to the Bills tab on mount.
  // BUG-11: a My Day task lands on Activity → Tasks with that task opened.
  useEffect(() => {
    if (!initialTab) return;
    setActiveTab(toTab(initialTab));
    if (initialTab === "tasks") setFocusSub("tasks");
  }, [initialTab, focusBillId, focusTaskId]);

  if (loading) return <div className="page loading">Loading claim...</div>;
  if (!claim)  return <div className="page empty-state">Claim not found</div>;

  const vca = claim.vca_data || {};
  // OPS-3: Bills tab is visible to bill-intake admins, or to adjusters/managers
  // who hold a claim-level review/approve capability on THIS claim.
  const _u = getUser();
  const billsVisible = can("view_bill_intake")
    || canOnClaim("review_bills", claim, _u) || canOnClaim("approve_bills", claim, _u);
  // WORKBENCH-INSURED-LIFT (2026-05-11): Insured = policy-holding company
  // sourced from claim_parties role='Insured' (new claim.insured_company_name
  // field on /claims/{id} response, canonical post Migration 039).  Falls
  // back to vca.Insurer only when claim_parties hasn't been populated for
  // the claim yet (transitional — see INSURED-LIFT-FOLLOWUP-RECONCILE-FK).
  // Claimant = injured person. Canonical claimant_name (claimant_details-first,
  // resolved server-side; LD-CLAIMANT-001) so a claimant entered via the Claimant
  // tab shows in the header, not just legacy claims.insured_name.
  const insuredCompany = claim.insured_company_name || vca.Insurer || "";
  const claimantName   = claim.claimant_name || claim.insured_name || vca.Claimant_Name || vca.claimant_name || vca.Insured || "";
  const title = [insuredCompany, claimantName].filter(Boolean).join(" — ") || claim.claim_number;

  // Compact meta row.  Filter out empties so the row doesn't render blank
  // pills when a field is missing.  DOL + Adjuster + Vessel are the most
  // operationally useful fields per Kuhrt; the rest stay in the General tab.
  const dol     = fmt.date(claim.date_of_loss || vca.Date_Of_Loss);
  const leadAdj = claim.lead_adjuster_name || vca.Assigned_Adjuster;
  const vessel  = claim.vessel_name || vca.Vessel;
  const metaPills = [
    dol     && { label: "DOL",      value: dol },
    leadAdj && { label: "Adjuster", value: leadAdj },
    vessel  && { label: "Vessel",   value: vessel },
  ].filter(Boolean);

  return (
    <div className="page claim-page">
      <div className="claim-header">
        <div className="claim-header-row">
          <button className="back-btn" onClick={onBack} title="Back to Workbench">← Back</button>
          <div className="claim-header-id">
            <span className="claim-number">{claim.claim_number}</span>
            <span className="claim-title">{title}</span>
          </div>
          <ReadinessHeaderCheck claimId={claim.id} />
          <span className="badge"
                style={{
                  background: `${statusColor(claim.claim_status)}35`,
                  color: "white",
                  fontSize: 11,
                  padding: "3px 10px",
                  border: `1px solid ${statusColor(claim.claim_status)}60`,
                  flexShrink: 0,
                }}>
            {claim.claim_status}{claim.litigation_flag && " ⚠ LITIGATION"}
          </span>
          <BordereauReadinessChip claimId={claim.id} />
          <div className="header-actions">
            <button className="btn btn-blue">+ Invoice</button>
            <button className="btn">Print</button>
            <VoidControls claim={claim} onClaimUpdated={setClaim} />
          </div>
        </div>
        {(metaPills.length > 0 || claim.is_hipaa_sensitive) && (
          <div className="claim-header-row-meta">
            {metaPills.map((p, i) => (
              <span key={p.label} className="meta-pill">
                {i > 0 && <span className="meta-sep">·</span>}
                <span className="meta-label">{p.label}</span>
                <span className="meta-value">{p.value}</span>
              </span>
            ))}
            {claim.is_hipaa_sensitive && (
              <span className="meta-pill">
                <span className="meta-sep">·</span>
                <span className="hipaa-flag">⚠ HIPAA Sensitive</span>
              </span>
            )}
          </div>
        )}
      </div>

      {claim.voided_at && (
        <div style={{ margin: "8px 0", padding: "9px 14px", background: "#fef2f2", border: "1px solid #fecaca", borderRadius: 6, color: "#991b1b", fontSize: 13, display: "flex", alignItems: "center", gap: 8 }}>
          <strong style={{ letterSpacing: ".03em" }}>⛔ VOIDED</strong>
          <span>Hidden from lists, search, reports &amp; pickers{claim.voided_by_name ? ` · voided by ${claim.voided_by_name}` : ""}{claim.void_reason ? ` · “${claim.void_reason}”` : ""}. {can("void_claim") ? "Use Un-void to restore." : ""}</span>
        </div>
      )}

      {/* CLAIM-OVERVIEW-UX-2: attention lives ONLY in the Overview command rail
          (Next Actions). It is intentionally NOT rendered above the tabs on other
          tabs — that displaced the tab bar; the tab control stays put everywhere. */}

      <RelatedMatters claim={claim} onClaimUpdated={setClaim} />

      <div className="tabs">
        {TABS.map(t => (
          <button key={t.id}
                  className={`tab-btn ${activeTab === t.id ? "active" : ""}`}
                  onClick={() => setActiveTab(t.id)}>
            {t.label}
          </button>
        ))}
      </div>

      {/* UI-SWEEP-CLAIM (2026-07-02, Design Standard Part VI): the financial
          summary ribbon is PERSISTENT — one ribbon, every tab, so the money
          picture never disappears when switching rooms. Lifted from
          FinancialsWorkspace (which no longer renders its own copy).
          Hidden on Overview: the cockpit already carries the trio in its
          Claim Financials tile; a second strip there is duplication. */}
      {activeTab !== "overview" && (
        <ClaimFinancialsSummary claimId={claimId} refreshKey={workRefresh} isTpa={claim?.matter_type === "tpa"} activeTab={activeTab} />
      )}

      {activeTab === "overview"   && <OverviewTab claim={claim} vca={vca} onClaimUpdated={setClaim} claimId={claimId} openTask={openTask} openTime={openTime} claimLabel={claim?.claim_number} workRefresh={workRefresh} goTab={goWithSub} onAttnGo={(it) => { const s = ATTN_SUB[it.type]; if (s) goWithSub(s); }}/>}
      {activeTab === "activity"   && <ActivityWorkspace key={focusTaskId ? `t${focusTaskId}` : "act"} claimId={claimId} claim={claim} openTask={openTask} claimLabel={claim?.claim_number} workRefresh={workRefresh} onWorkChanged={onWorkChanged} initialSub={ACT_SUBS.has(focusSub) ? focusSub : undefined} focusTaskId={focusTaskId}/>}
      {activeTab === "claim"      && <><GeneralTab claim={claim} vca={vca} onClaimUpdated={setClaim} embedded onWorkChanged={onWorkChanged} /><ClaimLoss claim={claim} onClaimUpdated={setClaim} onWorkChanged={onWorkChanged} /></>}
      {activeTab === "claimant"   && <ClaimantTab claimId={claimId} claim={claim} onClaimUpdated={setClaim} onWorkChanged={onWorkChanged} />}
      {activeTab === "parties"    && <PartiesTab claimId={claimId} claim={claim} workRefresh={workRefresh} onWorkChanged={onWorkChanged} />}
      {activeTab === "coverage"   && <PolicyCoverageTab claimId={claimId} workRefresh={workRefresh} onWorkChanged={onWorkChanged} />}
      {activeTab === "financials" && <FinancialsWorkspace claim={claim} claimId={claimId} openTime={openTime} claimLabel={claim?.claim_number} workRefresh={workRefresh} focusBillId={focusBillId} onWorkChanged={onWorkChanged} billsVisible={billsVisible} initialSub={FIN_SUBS.has(focusSub) ? focusSub : (initialTab==="bills"?"bills":undefined)}/>}
      {activeTab === "timelog"    && <TimeExpensesTab claimId={claimId} openTime={openTime} claimLabel={claim?.claim_number} workRefresh={workRefresh} onWorkChanged={onWorkChanged}/>}
      {activeTab === "documents"  && <DocumentsEmailsTab claimId={claimId} claim={claim}/>}
      {activeTab === "reports"    && <ReportsWorkspace claimId={claimId} claim={claim}/>}
    </div>
  );
}
